import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {lastValueFrom, Observable, Subject} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Branding} from '@models/adminstration/organization/branding/branding';
import {
  BRANDING_IMAGES_SETTINGS
} from '@constants/administration/organization/branding/branding-images-settings.constant';
import {BrandingImageTypes} from '@constants/administration/organization/branding/branding-image-types';
import {BrandingImagePopoverDetails} from '@models/adminstration/organization/branding/branding-image-popover-details';
import {BrandingImageDimension} from '@models/adminstration/organization/branding/branding-image-settings';
import {NotificationService} from '@services/notification/notification.service';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {BRANDING_DEFAULT_COLORS} from '@constants/administration/organization/branding/branding-default-colors';
import {BrandingColors} from '@models/adminstration/organization/branding/branding-colors';
import {BrandingImages} from '@models/adminstration/organization/branding/branding-images';
import {StorageService} from '@services/storage/storage.service';
import {DESIGN_PREVIEW_MODAL_ID, DESIGN_PREVIEW_STATE} from '@constants/modal-options/modal-options';
import {User} from '@models/user/user';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {switchMap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class BrandingService {
  private readonly BASE_URL: string;
  ORGANIZATION_BRANDING_URL_SUFFIX: string = '/api/web/admin/tenant/:id/branding';

  ORGANIZATION_BRANDING_STYLE_TAG_ID: string = 'organization-style';

  ORGANIZATION_BRANDING_CACHE_KEY: string = 'OrganizationBranding';

  CURRENT_PAGE_NAME = 'BrandingService';

  readonly SECONDARY_COLOR_ACTIVE = 'secondary-color-active';


  organizationBranding: Branding = new Branding();
  parentOrganizationBranding: Branding = new Branding();
  currentUserBranding: Branding = new Branding();
  previewOrganizationName: string = '';

  constructor(private firebaseAuthenticationService: FirebaseAuthenticationService,
              private translation: TranslateService,
              private notification: NotificationService,
              private storageService: StorageService,
              private http: HttpClient,
              private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public getOrganizationBranding(organizationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.ORGANIZATION_BRANDING_URL_SUFFIX;
    url = url.replace(':id', String(organizationId));

    return this.http.get<Branding>(url, HTTPS_REQUEST_OPTIONS);
  }

  public saveOrganizationBranding(organizationId: number, branding: Branding): Observable<any> {
    let url: string = this.BASE_URL + this.ORGANIZATION_BRANDING_URL_SUFFIX + '/save';
    url = url.replace(':id', String(organizationId));

    return this.http.post<Branding>(url, branding, HTTPS_REQUEST_OPTIONS);
  }

  public applyOrganizationStyles(organizationId: number, rewriteBranding: boolean) {
    const organizationBranding = this.storageService.readValueFromLocalStorage(this.ORGANIZATION_BRANDING_CACHE_KEY);
    if (organizationBranding) {
      this.changeDefaultOrganizationStyle(JSON.parse(organizationBranding), rewriteBranding);
    } else {
      this.getOrganizationBranding(organizationId).subscribe({
        next: (branding: Branding) => {
          if (!branding.colors && !branding.images) {
            return;
          }
          this.changeDefaultOrganizationStyle(branding, rewriteBranding);
          this.storageService.writeValueToLocalStorage(this.ORGANIZATION_BRANDING_CACHE_KEY, JSON.stringify(branding));
        },
        error: (error) => {
          console.log(error);
        }
       });
    }
  }

  public resetCachedOrganizationStyle() {
    this.storageService.deleteByKeyFromLocalStorage(this.ORGANIZATION_BRANDING_CACHE_KEY);
  }

  public removeOrganizationBranding() {
    const htmlStyleElement = document.getElementById(this.ORGANIZATION_BRANDING_STYLE_TAG_ID);
    if (htmlStyleElement) {
      document.head.removeChild(htmlStyleElement);
    }
  }

  public getImagePopoverDetailsByType(imageType: string): BrandingImagePopoverDetails {

    let title: string = '', type: string = '', w: number = 0, h: number = 0;

    switch (imageType) {
      case BrandingImageTypes.LOGO: {
        title = BRANDING_IMAGES_SETTINGS.LOGO.label;
        type = BRANDING_IMAGES_SETTINGS.LOGO.type.toUpperCase();
        w = BRANDING_IMAGES_SETTINGS.LOGO.size.w;
        h = BRANDING_IMAGES_SETTINGS.LOGO.size.h;
        break;
      }
      case BrandingImageTypes.LOGO_MOBILE: {
        title = BRANDING_IMAGES_SETTINGS.LOGO_MOBILE.label;
        type = BRANDING_IMAGES_SETTINGS.LOGO_MOBILE.type.toUpperCase();
        w = BRANDING_IMAGES_SETTINGS.LOGO_MOBILE.size.w;
        h = BRANDING_IMAGES_SETTINGS.LOGO_MOBILE.size.h;
        break;
      }
      case BrandingImageTypes.FAV_ICON: {
        title = BRANDING_IMAGES_SETTINGS.FAV_ICON.label;
        type = BRANDING_IMAGES_SETTINGS.FAV_ICON.type.toUpperCase();
        w = BRANDING_IMAGES_SETTINGS.FAV_ICON.size.w;
        h = BRANDING_IMAGES_SETTINGS.FAV_ICON.size.h;
        break;
      }
    }
    let content = this.translation.instant(
        'Required characteristics:<br> - Type: *.#type <br/>- Size: #w x #h pixels<br/>Recommended characteristics: </br>- transparent background');
    content = content.replace('#type', type);
    content = content.replace('#w', w);
    content = content.replace('#h', h);

    return new BrandingImagePopoverDetails(title, content);
  }

  public getOrganizationBrandingOrFallback(branding: Branding, parentBranding: Branding | null): Branding {
    const defaultBranding = this.getDefaultBranding();
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'colors');
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'colors', 'primary');
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'colors', 'highlight');
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'colors', 'success');
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'colors', 'warning');
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'colors', 'info');
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'colors', 'danger');
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'images');
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'images', 'logo');
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'images', 'logoMobile');
    branding = this.getBrandingComponentOrFallback(branding, parentBranding, defaultBranding, 'images', 'favIcon');
    return branding;
  }

  public verifyAndSetImageRequiredDimensions(source: string, controlName: string,
                                             imageLabel: string, recommendedSize: BrandingImageDimension): Subject<string> {
    const imageContentObservable = new Subject<string>();
    const image = new Image();
    image.src = source;
    image.onload = () => {
      const currentSize = new BrandingImageDimension(image.width, image.height);
      let imageContent;

      if (currentSize.height > recommendedSize.height || currentSize.width > recommendedSize.width) {
        let message = this.translation.instant('The actual image size (#size pixels), exceed the recommended ' +
            'size (#recomended pixels). The image will be resized while keeping aspect ratio.');
        message = message.replace('#size', currentSize.width + ' x ' + currentSize.height);
        message = message.replace('#recomended', recommendedSize.width + ' x ' + recommendedSize.height);
        this.notification.warning(message, 'BrandingImageComponent', imageLabel);
        imageContent = this.imageResize(image, controlName, currentSize, recommendedSize);
      } else {
        imageContent = source;
      }
      imageContentObservable.next(imageContent);
      imageContentObservable.complete();
    };

    image.onerror = (e) => {
      console.warn('Failed to load image for dimension verification. Falling back to original source.', source, e);
      imageContentObservable.next(source);
      imageContentObservable.complete();
    };
    return imageContentObservable;
  }

  public changeDefaultOrganizationStyle(branding: Branding, rewriteBranding: boolean) {
    if (branding.secondaryColorEnabled) {
      this.enableSecondaryColor();
    } else {
      this.disableSecondaryColor();
    }

    let htmlStyleElement = document.getElementById(this.ORGANIZATION_BRANDING_STYLE_TAG_ID);

    if (!htmlStyleElement) {
      htmlStyleElement = document.createElement('style');
      htmlStyleElement.id = this.ORGANIZATION_BRANDING_STYLE_TAG_ID;
      document.head.appendChild(htmlStyleElement);
    } else {
      if (!rewriteBranding && htmlStyleElement) {
        return;
      }
    }

    htmlStyleElement.innerHTML = '';
    htmlStyleElement.innerHTML += ':root {';

    if (branding.colors) {
      htmlStyleElement.innerHTML += this.generateColorVariables(branding.colors);
    }

    if (branding.images) {
      htmlStyleElement.innerHTML += this.generateLogoVariables(branding.images);
    }

    htmlStyleElement.innerHTML += '}';

    if (branding.images && branding.images.favIcon) {
      this.changeFavIcon(branding.images.favIcon);
    }

  }

  async initializeBranding(branding: Branding, id: number) {
    const designPreview = await this.getDesignPreviewFromIndexedDb();
    this.organizationBranding = (designPreview && id === designPreview.id) ? designPreview : branding;
  }

  async discardDesignPreview() {
    const result = await this.storageService.readValueFromIndexedDb(DESIGN_PREVIEW_STATE);
    if (result) {
      this.getOrganizationBranding(JSON.parse(result as string).id).subscribe((branding) => {
        this.organizationBranding = branding;
      });
    }
    this.getDefaultOrganizationBranding().subscribe((branding: Branding) => {
      this.resetCachedOrganizationStyle();
      this.changeDefaultOrganizationStyle(branding, true);
    });

    this.deleteDesignPreviewFromIndexedDb();
  }

  async applyDesignPreview() {
    try {
      const designPreview = await this.getDesignPreviewFromIndexedDb();
      const branding = await lastValueFrom(this.saveOrganizationBranding(designPreview.id, designPreview));
      const defaultBranding = await lastValueFrom(this.getDefaultOrganizationBranding());

      const user: User = await lastValueFrom(this.firebaseAuthenticationService.getLoggedUserDetails());
      if (user.parentId === branding.id) {
        this.changeDefaultOrganizationStyle(branding, true);
        this.storageService.writeValueToLocalStorage(this.ORGANIZATION_BRANDING_CACHE_KEY, JSON.stringify(branding));
      } else {
        this.changeDefaultOrganizationStyle(defaultBranding, true);
      }

      this.notification.success(this.translation.instant('Success updating organization branding details'), 'name', false);
    } catch (error: any) {
      const branding = await lastValueFrom(this.getDefaultOrganizationBranding());
      this.changeDefaultOrganizationStyle(branding, true);
      this.notification.error(this.translation.instant('There was an error updating organization branding details'), error, 'name', true);
    } finally {
      this.deleteDesignPreviewFromIndexedDb();
    }
  }

  private async getDesignPreviewFromIndexedDb(): Promise<any> {
    const result = await this.storageService.readValueFromIndexedDb(DESIGN_PREVIEW_STATE);
    return result ? JSON.parse(result as string) : null;
  }

  private getDefaultOrganizationBranding(): Observable<Branding> {
    return this.firebaseAuthenticationService.getLoggedUserDetails().pipe(
        switchMap((user: User) => {
          return this.getOrganizationBranding(user.parentId);
        })
    );
  }

  private deleteDesignPreviewFromIndexedDb(): void {
    this.storageService.deleteByKeyFromIndexDb(DESIGN_PREVIEW_STATE);
  }

  closePreviewToast() {
    const previewToast = this.notification.toasts.find(t => t.id === DESIGN_PREVIEW_MODAL_ID) as any;
    if (previewToast) {
      this.notification.remove(previewToast);
      this.discardDesignPreview();
    }
  }

  disableSecondaryColor() {
    document.body.classList.remove(this.SECONDARY_COLOR_ACTIVE);
  }

  private enableSecondaryColor() {
    document.body.classList.add(this.SECONDARY_COLOR_ACTIVE);
  }

  private getBrandingComponentOrFallback(branding: Branding, parentBranding: Branding | null, defaultBranding: Branding,
                                         property: string, subProperty?: string): Branding {
    if (!subProperty) {
      if (branding[property]) {
        return branding;
      }
      if (parentBranding && parentBranding[property]) {
        branding[property] = parentBranding[property];
      } else {
        branding[property] = defaultBranding[property];
      }
    } else {
      if (branding[property] && branding[property][subProperty] && branding[property][subProperty].trim().length > 0) {
        return branding;
      }
      if (parentBranding !== null && parentBranding[property] && parentBranding[property][subProperty]) {
        branding[property][subProperty] = parentBranding[property][subProperty].trim();
      } else {
        branding[property][subProperty] = defaultBranding[property][subProperty].trim();
      }
    }

    return branding;
  }

  private getDefaultBranding(): Branding {
    const branding = new Branding();
    branding.colors.primary = BRANDING_DEFAULT_COLORS.PRIMARY;
    branding.colors.highlight = BRANDING_DEFAULT_COLORS.HIGHLIGHT;
    branding.colors.success = BRANDING_DEFAULT_COLORS.SUCCESS;
    branding.colors.warning = BRANDING_DEFAULT_COLORS.WARNING;
    branding.colors.info = BRANDING_DEFAULT_COLORS.INFO;
    branding.colors.danger = BRANDING_DEFAULT_COLORS.DANGER;

    branding.images.logo = BRANDING_IMAGES_SETTINGS.LOGO.url;
    branding.images.logoMobile = BRANDING_IMAGES_SETTINGS.LOGO_MOBILE.url;
    branding.images.favIcon = BRANDING_IMAGES_SETTINGS.FAV_ICON.url;

    return branding;
  }

  private imageResize(image, controlName: string, currentSize: BrandingImageDimension,
                      requiredSize: BrandingImageDimension): string {

    const newSize: BrandingImageDimension = this.calculateAspectRatioFit(currentSize, requiredSize);
    const canvas = this.createCanvas('canvas_' + controlName + '_resize', newSize.width, newSize.height);
    const canvasContext = canvas.getContext('2d');
    if (canvasContext !== null) {
      canvasContext.drawImage(image, 0, 0, newSize.width, newSize.height);
      canvas.remove();
      return canvasContext.canvas.toDataURL();
    } else {
      canvas.remove();
      return '';
    }
  }

  private createCanvas(canvasId: string, width: number, height: number) {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('id', canvasId);
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  private calculateAspectRatioFit(currentSize: BrandingImageDimension,
                                  requiredSize: BrandingImageDimension): BrandingImageDimension {
    const maxWidth: number = requiredSize.width; // Max width for the image
    const maxHeight: number = requiredSize.height;

    const width: number = currentSize.width;  // Current image width
    const height: number = currentSize.height;

    const ratio: number = Math.min(maxWidth / width,
        maxHeight / height);
    return new BrandingImageDimension(Math.ceil(width * ratio), Math.ceil(height * ratio));

  }

  private generateLogoVariables(images: BrandingImages) {
    return `
      --dyn-images-logo: url(' ${images.logo} ');
      --dyn-images-logo-mobile: url(' ${images.logoMobile} ');
      `;
  }

  private generateColorVariables(colors: BrandingColors) {
    let parentColors: BrandingColors | undefined;
    if (this.parentOrganizationBranding) {
      parentColors = this.parentOrganizationBranding.colors;
    }
    return `
      --dyn-primary: ${colors.primary};
      --dyn-primary-rgb: ${this.hexToRgb(colors.primary)};
      --dyn-secondary: ${colors.secondary ?? colors.primary};
      --dyn-secondary-rgb: ${this.hexToRgb(colors.secondary ?? colors.primary)};
      --dyn-highlight: ${colors.highlight};
      --dyn-highlight-rgb: ${this.hexToRgb(colors.highlight)};
      --dyn-success: ${colors.success};
      --dyn-success-rgb: ${this.hexToRgb(colors.success)};
      --dyn-warning: ${colors.warning};
      --dyn-warning-rgb: ${this.hexToRgb(colors.warning)};
      --dyn-info: ${colors.info};
      --dyn-info-rgb: ${this.hexToRgb(colors.info)};
      --dyn-danger: ${colors.danger};
      --dyn-danger-rgb: ${this.hexToRgb(colors.danger)};` + (parentColors ? `--dyn-design-preview-toast: ${parentColors.primary};--dyn-design-preview-toast-rgb: ${this.hexToRgb(parentColors.primary)};` : '');

  }

  private changeFavIcon(src: string) {
    const oldLink = document.querySelector('link[rel*=\'icon\']');
    const link = document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = src;

    if (oldLink) {
      document.head.removeChild(oldLink);
    }
    document.head.appendChild(link);
  }

  private hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      const r = parseInt(result[1], 16);
      const g = parseInt(result[2], 16);
      const b = parseInt(result[3], 16);

      return r + ',' + g + ',' + b;
    }
    return null;
  }

}
